/**
 * Operate status vocabulary — one tone + one human label per status family.
 *
 * GUIDELINES §4: success = done/verified, info = in progress (pulse),
 * warning = waiting on a person / medium risk, error = failed / high risk,
 * policy = governance hold, neutral = queued / idle / not started.
 *
 * Every Operate screen maps statuses through these helpers so the same
 * incident, EI run, step or check never reads differently on two tabs.
 */
import { useTheme } from '@mui/material/styles';
import { BORDER, FONT, RADIUS, STATUS_HEX, SURFACE, type StatusTone } from '../../../design-system/foundation';
import {
  incidentStatusLabel as mcIncidentStatusLabel,
  mapExecutionPhase,
  mapIncidentStatus,
  type McStatus,
} from '../../../mission-control/tokens';
import { MC_TONE } from '../../../mission-control/primitives';
import { eiStateLabel } from '../../../lib/eiLifecycle';

/** "NEEDS_HUMAN" / "needs-human" / "Needs Human" → "Needs human". */
export function sentenceCase(value?: string | null): string {
  const s = String(value ?? '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!s) return '';
  const lower = s.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/* ── Severity / risk ───────────────────────────────────────────────── */

/** critical/high → error, medium → warning, low → info. */
export function severityTone(severity?: string | null): StatusTone {
  const s = String(severity ?? '').toLowerCase();
  if (s.startsWith('crit') || s === 'p0' || s === 'p1' || s === 'sev1' || s === 'high') return 'error';
  if (s.startsWith('med') || s === 'moderate' || s === 'p2' || s === 'warning' || s === 'warn') return 'warning';
  if (s === 'low' || s === 'minor' || s === 'p3' || s === 'p4' || s === 'info') return 'info';
  return 'neutral';
}

export function severityLabel(severity?: string | null): string {
  return sentenceCase(severity) || 'Unknown';
}

/** Literal hex for severity bars / chart fills. */
export function severityHex(severity?: string | null): string {
  return STATUS_HEX[severityTone(severity)];
}

/** Risk levels share the severity mapping. */
export const riskTone = severityTone;

/* ── Incidents ─────────────────────────────────────────────────────── */

export function incidentStatusTone(status?: string | null, stage?: string | null, closed?: boolean): StatusTone {
  // mapIncidentStatus separates "needs a person" (waiting → warning) from open/queued work (queued → neutral).
  return MC_TONE[mapIncidentStatus(status ?? undefined, stage ?? undefined, closed)];
}

export function incidentStatusLabel(status?: string | null, stage?: string | null, closed?: boolean): string {
  return mcIncidentStatusLabel(status ?? undefined, stage ?? undefined, closed);
}

/* ── Engineering Intelligence run state ────────────────────────────── */

/** One human label for an EI run state ("Needs you", "Awaiting approval", …). */
export function eiStateHumanLabel(state?: string | null): string {
  if (!state) return 'No run';
  const s = String(state).toUpperCase();
  if (s === 'NEEDS_HUMAN') return 'Needs you';
  if (s === 'PLATFORM_BLOCKED') return 'Platform issue';
  if (s === 'FAILED' || s === 'ERROR') return 'Failed';
  return sentenceCase(eiStateLabel(state));
}

export function eiStateTone(state?: string | null): StatusTone {
  const s = String(state ?? '').toUpperCase();
  if (!s) return 'neutral';
  if (s === 'NEEDS_HUMAN' || s === 'AWAITING_APPROVAL' || s === 'IMPLEMENTATION_PACKAGE_READY') return 'warning';
  if (s === 'RESOLVED' || s === 'CLOSED') return 'success';
  if (s === 'PLATFORM_BLOCKED') return 'error';
  if (s.includes('FAIL') || s === 'ERROR' || s === 'REJECTED') return 'error';
  if (s === 'NEW_INCIDENT') return 'neutral';
  return 'info';
}

/** Running EI states pulse. */
export function eiStatePulse(state?: string | null): boolean {
  return eiStateTone(state) === 'info';
}

/* ── Execution steps ───────────────────────────────────────────────── */

/**
 * Filmstrip / timeline step status → Mission Control status. Wraps
 * mapExecutionPhase so the filmstrip and the Inspector agree, and maps
 * the step vocabulary it doesn't know (`active`, `pending`, `skipped`).
 */
export function executionStepStatus(status?: string | null): McStatus {
  const s = String(status ?? '').toLowerCase();
  if (s === 'active' || s === 'in_progress' || s === 'in-progress') return 'running';
  if (s === 'pending' || s === 'queued' || s === 'skipped' || s === 'idle' || s === 'not_started') return 'queued';
  return mapExecutionPhase(s);
}

export function executionStepTone(status?: string | null): StatusTone {
  return MC_TONE[executionStepStatus(status)];
}

/* ── Checks / tests ────────────────────────────────────────────────── */

/** pass → success, fail → error, running → info, waiting on a person → warning, else neutral. */
export function checkResultTone(status?: string | boolean | null): StatusTone {
  if (status === true) return 'success';
  if (status === false) return 'error';
  const s = String(status ?? '').toLowerCase();
  if (!s) return 'neutral';
  if (['pass', 'passed', 'success', 'succeeded', 'ok', 'green', 'verified', 'completed', 'complete', 'promoted', 'approved'].some((k) => s === k))
    return 'success';
  if (s.includes('fail') || s.includes('error') || s === 'red' || s === 'rejected' || s.includes('regress')) return 'error';
  if (['running', 'executing', 'in_progress', 'in-progress', 'validating', 'active'].some((k) => s.includes(k))) return 'info';
  if (s === 'partial' || s === 'inconclusive') return 'warning';
  if (s === 'not_linked') return 'info';
  if (s.includes('await') || s.includes('needs') || s.includes('review') || s === 'blocked' || s === 'flaky' || s === 'warn' || s === 'warning')
    return 'warning';
  if (s.includes('policy')) return 'policy';
  return 'neutral';
}

export function checkResultLabel(status?: string | boolean | null): string {
  if (status === true) return 'Passed';
  if (status === false) return 'Failed';
  return sentenceCase(String(status ?? '')) || 'Not run';
}

/* ── Mission type ──────────────────────────────────────────────────── */

/** OPERATIONAL → "Operational". Render with a neutral StatusBadge (no dot). */
export function missionTypeLabel(type?: string | null): string {
  return sentenceCase(type) || 'Unclassified';
}

/* ── Operational recovery (ops pipeline) ───────────────────────────── */

export function opsRecoveryTone(status?: string | null): StatusTone {
  const s = String(status ?? '').toLowerCase();
  if (s === 'verified' || s === 'closed' || s === 'resolved') return 'success';
  if (s === 'failed' || s === 'rolled_back') return 'error';
  if (s === 'needs_human' || s === 'awaiting_upstream' || s.startsWith('await') || s === 'concluded_unresolved')
    return 'warning';
  if (!s || s === 'idle' || s === 'not_started' || s === 'pending') return 'neutral';
  return 'info';
}

/* ── Surfaces ──────────────────────────────────────────────────────── */

/** Theme-aware code / log well (replaces hardcoded #0c0f16 wells). */
export const CODE_WELL_SX = {
  bgcolor: SURFACE.card,
  color: 'text.primary',
  border: `1px solid ${BORDER.subtle}`,
  borderRadius: `${RADIUS.control}px`,
  fontFamily: FONT.mono,
  fontSize: '0.8125rem',
  lineHeight: 1.55,
} as const;

/** Monaco theme that follows the MUI palette mode. */
export function useMonacoTheme(): 'vs' | 'vs-dark' {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? 'vs-dark' : 'vs';
}
